import { z } from 'zod';
import { payableStageSchema } from '../constants/stages.js';
import { financialYearOf } from '../money/financial-year.js';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { paymentMethodSchema } from '../money/payment-methods.js';
import { dateInputSchema, isoDateStringSchema, objectIdSchema } from './common.js';

/**
 * What a karigar has EARNED, what they have been PAID, and what is therefore OUTSTANDING
 * (task 21) — three separate facts, kept separate on purpose.
 *
 * **Earnings are not a collection.** A completed assignment already carries the rate that was
 * captured when the work was handed over (task 20) and a null `settledAt`; that pair IS the
 * unsettled earning. Copying it into an "earnings" table would create a second number for the
 * same fact, and the day the two disagree there is no way to say which is right.
 *
 * **A payment is its own fact.** A settlement records what was actually handed over, on what
 * date, by what method — and stamps the jobs it paid for. Nothing derives one from the other,
 * which is why the system can answer all three questions at any moment rather than two of them
 * and a subtraction.
 *
 * **The arithmetic is stated once, here, in integer paise**, so the screen that shows an owner a
 * figure before they pay it and the server that writes the payout are running the same lines of
 * code. `payoutNetPaise` is the whole of it:
 *
 *     net = jobs + additions − deductions − advance adjusted
 *
 * and the identity that has to hold for every person, for ever, is
 *
 *     paid + outstanding = earned + additions − deductions
 *
 * which `payouts.test.ts` asserts to the paisa against a shop that has been settled twice.
 */

// --- Vocabulary ---------------------------------------------------------------------------

/**
 * The three things that move money between a shop and a karigar, besides the work itself.
 *
 *  - `advance`   money handed over BEFORE the work is settled. Common in this trade, and the
 *                reason it is a first-class row rather than a negative deduction: it is cash
 *                that has genuinely left the shop, so it reaches the P&L on the day it left.
 *  - `addition`  a bonus. "Finished the rush order on Saturday."
 *  - `deduction` a stoppage. "Damaged the piece."
 */
export const PAYOUT_ENTRY_KINDS = ['advance', 'addition', 'deduction'] as const;
export type PayoutEntryKind = (typeof PAYOUT_ENTRY_KINDS)[number];
export const payoutEntryKindSchema = z.enum(PAYOUT_ENTRY_KINDS);

/** The two an owner types on the settlement screen. An advance has its own form and its own date. */
export const PAYOUT_ADJUSTMENT_KINDS = ['addition', 'deduction'] as const;
export type PayoutAdjustmentKind = (typeof PAYOUT_ADJUSTMENT_KINDS)[number];
export const payoutAdjustmentKindSchema = z.enum(PAYOUT_ADJUSTMENT_KINDS);

export const PAYOUT_REASON_MAX_LENGTH = 200;
export const PAYOUT_NOTE_MAX_LENGTH = 300;

/**
 * Why the figure is not simply the sum of the jobs.
 *
 * Required, and short-form validated rather than optional, because the whole point of showing a
 * deduction on a statement is that the tailor can read what it was for. "₹200 deducted" with no
 * reason is the argument this module exists to prevent.
 */
export const payoutReasonSchema = z
  .string()
  .trim()
  .min(3, 'Say what this is for')
  .max(PAYOUT_REASON_MAX_LENGTH);

const noteSchema = z.string().trim().max(PAYOUT_NOTE_MAX_LENGTH);

/** The prefix a payout number is built with: `PAY/26-27/0001`. */
export const PAYOUT_NUMBER_PREFIX = 'PAY';

/**
 * The counter a payout number is drawn from: one gapless sequence per shop, per financial year.
 * Drawn INSIDE the settlement transaction, exactly as an invoice number is — a settlement that
 * fails returns its number by never having taken it.
 */
export function payoutCounterKey(settledAt: Date): string {
  return `payout:${financialYearOf(settledAt)}`;
}

// --- The arithmetic ------------------------------------------------------------------------

export interface PayoutComponents {
  /** The sum of the job amounts being settled. Never negative. */
  jobsPaise: number;
  additionsPaise: number;
  deductionsPaise: number;
  /** How much of an outstanding advance this settlement takes back. */
  advanceAdjustedPaise: number;
}

/** What actually gets handed over. The one definition, shared by the screen and the server. */
export function payoutNetPaise(components: PayoutComponents): number {
  return (
    components.jobsPaise +
    components.additionsPaise -
    components.deductionsPaise -
    components.advanceAdjustedPaise
  );
}

/**
 * What a settlement can take back off an outstanding advance.
 *
 * Never more than the settlement is worth: a karigar who took ₹5,000 against a fortnight that
 * only earned ₹3,000 is settled at zero and still owes ₹2,000 against the next one. Recovering
 * the difference by handing them a NEGATIVE payout would be inventing a debt collection the
 * shop did not make.
 */
export function advanceAdjustmentPaise(
  outstandingAdvancePaise: number,
  settlementBeforeAdvancePaise: number,
): number {
  return Math.max(0, Math.min(outstandingAdvancePaise, settlementBeforeAdvancePaise));
}

/**
 * Spread an adjustment across the advances it pays off, oldest first, and never past the total.
 *
 * Oldest first because that is what a shop means by "adjust it against the next settlement" —
 * the ₹2,000 from last month clears before the ₹500 from Tuesday. Returning the plan rather
 * than applying it keeps the arithmetic testable without a database.
 */
export function planAdvanceApplications(
  advances: readonly { id: string; outstandingPaise: number }[],
  applyPaise: number,
): { id: string; amountPaise: number }[] {
  const plan: { id: string; amountPaise: number }[] = [];
  let left = applyPaise;
  for (const advance of advances) {
    if (left <= 0) break;
    const take = Math.min(left, advance.outstandingPaise);
    if (take <= 0) continue;
    plan.push({ id: advance.id, amountPaise: take });
    left -= take;
  }
  return plan;
}

// --- One job, as it appears on a settlement and on the statement ------------------------------

/**
 * A job on a payout: what it was, whose bill it came off, and what it paid.
 *
 * Everything here is COPIED onto the payout when it is settled. A statement printed next year
 * has to say what the tailor was paid for, and it cannot depend on the order still existing, the
 * garment type still being called what it was called, or the rate card being what it was.
 */
export const payoutJobSchema = z.object({
  assignmentId: objectIdSchema,
  orderId: objectIdSchema,
  orderNumber: z.string(),
  lineNumber: z.number().int().positive(),
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  customerName: z.string(),
  stage: payableStageSchema,
  quantity: z.number().int().positive(),
  /** Per piece, as captured on the assignment when the work was handed over. */
  ratePaise: nonNegativePaiseSchema,
  /** `ratePaise × quantity`. The column a tailor checks. */
  amountPaise: nonNegativePaiseSchema,
  completedAt: isoDateStringSchema,
});
export type PayoutJob = z.infer<typeof payoutJobSchema>;

/**
 * A job that is earned and not yet paid.
 *
 * Same shape as above but with a NULLABLE rate, because an unpriced job has to be visible on the
 * settlement screen — "nothing on the rate card prices a lehenga's finishing" is something the
 * owner must see and fix, not something that silently drops off the list the day it is paid.
 */
export const unsettledJobSchema = payoutJobSchema.extend({
  ratePaise: nonNegativePaiseSchema.nullable(),
  amountPaise: nonNegativePaiseSchema.nullable(),
});
export type UnsettledJob = z.infer<typeof unsettledJobSchema>;

// --- Additions, deductions and advances -------------------------------------------------------

export const payoutEntrySchema = z.object({
  id: objectIdSchema,
  kind: payoutEntryKindSchema,
  staffUserId: objectIdSchema,
  amountPaise: nonNegativePaiseSchema,
  /** Advances only: how much of it has already been taken back. */
  appliedPaise: nonNegativePaiseSchema,
  /** `amountPaise − appliedPaise`. Zero on a fully adjusted advance and on every adjustment. */
  outstandingPaise: nonNegativePaiseSchema,
  /** Required on an addition or a deduction; the free-text note on an advance. */
  reason: z.string().nullable(),
  /** How an advance was handed over. Null on an adjustment, which moves no money on its own. */
  method: paymentMethodSchema.nullable(),
  occurredAt: isoDateStringSchema,
  /** The settlement that consumed it, once one has. */
  settledPayoutId: objectIdSchema.nullable(),
  settledAt: isoDateStringSchema.nullable(),
  recordedByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type PayoutEntry = z.infer<typeof payoutEntrySchema>;

/** An adjustment as it prints on the statement — itemised, with its reason, beside the jobs. */
export const payoutAdjustmentSchema = z.object({
  entryId: objectIdSchema,
  kind: payoutAdjustmentKindSchema,
  amountPaise: nonNegativePaiseSchema,
  reason: z.string(),
  occurredAt: isoDateStringSchema,
  recordedByName: z.string().nullable(),
});
export type PayoutAdjustment = z.infer<typeof payoutAdjustmentSchema>;

/** How much of which advance this settlement took back, so the statement can show it line by line. */
export const advanceApplicationSchema = z.object({
  entryId: objectIdSchema,
  amountPaise: nonNegativePaiseSchema,
  /** When the advance was originally handed over. */
  advancedOn: isoDateStringSchema,
});
export type AdvanceApplication = z.infer<typeof advanceApplicationSchema>;

// --- The payout itself --------------------------------------------------------------------------

export const payoutSchema = z.object({
  id: objectIdSchema,
  /** `PAY/26-27/0001` — gapless per shop per financial year. */
  number: z.string(),
  staffUserId: objectIdSchema,
  /** Snapshotted: a karigar who leaves does not blank out last year's statements. */
  staffName: z.string(),
  /** The window the settled jobs fall in, taken from the jobs themselves. */
  periodFrom: isoDateStringSchema,
  periodTo: isoDateStringSchema,
  paidAt: isoDateStringSchema,
  method: paymentMethodSchema,
  note: z.string().nullable(),
  jobs: z.array(payoutJobSchema),
  adjustments: z.array(payoutAdjustmentSchema),
  advanceApplications: z.array(advanceApplicationSchema),
  /** The sum of `jobs[].amountPaise`, exactly. */
  jobsPaise: nonNegativePaiseSchema,
  additionsPaise: nonNegativePaiseSchema,
  deductionsPaise: nonNegativePaiseSchema,
  advanceAdjustedPaise: nonNegativePaiseSchema,
  /** What was handed over. Never negative — see `advanceAdjustmentPaise`. */
  netPaise: nonNegativePaiseSchema,
  /** Advances taken during the cycle, deducted from what is payable. */
  advancePaise: nonNegativePaiseSchema.optional(),
  /** Bonuses and allowances granted on top of what was earned. */
  addOnPaise: nonNegativePaiseSchema.optional(),
  /** `earned + addOn − advance`. Signed: an over-advanced cycle carries a negative into the next. */
  payablePaise: paiseSchema.optional(),
  /** The wage expense task 22 wrote for this payout. Null when the net came to nothing. */
  expenseId: objectIdSchema.nullable(),
  settledByName: z.string().nullable(),
  statementSentAt: isoDateStringSchema.nullable(),
  createdAt: isoDateStringSchema,
});
export type Payout = z.infer<typeof payoutSchema>;

export const payoutListSchema = z.object({
  items: z.array(payoutSchema),
  /** Every rupee in the list, so the header and the rows cannot disagree. */
  totalPaise: nonNegativePaiseSchema,
});
export type PayoutList = z.infer<typeof payoutListSchema>;

// --- The three questions, answered per person ---------------------------------------------------

/**
 * Earned, paid and outstanding — and the working that connects them.
 *
 * `earnedPaise` and `paidPaise` are bounded by the period asked for. The three OUTSTANDING
 * figures are as at today and are deliberately not: a debt does not belong to a month, and an
 * owner asking "what do I owe Ravi?" in September must not be shown a number that quietly
 * excludes August's unpaid work.
 */
export const payoutSummarySchema = z.object({
  /** Completed work in the period, at the rate captured on each job. */
  earnedPaise: nonNegativePaiseSchema,
  /** Of that, what has been settled. */
  settledPaise: nonNegativePaiseSchema,
  /** Completed, unsettled work — the jobs the settlement screen itemises. */
  unsettledPaise: nonNegativePaiseSchema,
  unsettledJobCount: z.number().int().nonnegative(),
  /** Completed jobs nothing on the rate card prices. They cannot be settled until they are. */
  unpricedJobCount: z.number().int().nonnegative(),
  /** Additions and deductions recorded and not yet applied to a settlement. */
  pendingAdditionsPaise: nonNegativePaiseSchema,
  pendingDeductionsPaise: nonNegativePaiseSchema,
  /** Advances handed over and not yet taken back. */
  advanceOutstandingPaise: nonNegativePaiseSchema,
  /** Cash actually handed over in the period: settlements plus advances. */
  paidPaise: nonNegativePaiseSchema,
  /**
   * What the shop owes as at today: unsettled work, plus pending additions, less pending
   * deductions, less any advance still to be recovered. Can be negative — a karigar who has
   * taken more in advance than they have earned is a real and ordinary state.
   */
  outstandingPaise: paiseSchema,
});
export type PayoutSummary = z.infer<typeof payoutSummarySchema>;

/** One person's whole ledger: the settlement screen, and their own earnings screen. */
export const staffLedgerSchema = z.object({
  staffUserId: objectIdSchema,
  staffName: z.string(),
  /** Null when the ledger covers everything this person has ever done. */
  from: isoDateStringSchema.nullable(),
  to: isoDateStringSchema,
  summary: payoutSummarySchema,
  /** Every earned-and-unpaid job, oldest first — itemised, with its rate. */
  unsettled: z.array(unsettledJobSchema),
  /** Additions and deductions waiting to be applied at the next settlement. */
  pendingEntries: z.array(payoutEntrySchema),
  /** Advances still to be recovered, oldest first. */
  outstandingAdvances: z.array(payoutEntrySchema),
  /** What has actually been paid, newest first. */
  payouts: z.array(payoutSchema),
});
export type StaffLedger = z.infer<typeof staffLedgerSchema>;

// --- The owner's view across everybody -----------------------------------------------------------

export const payoutOverviewRowSchema = z.object({
  staffUserId: objectIdSchema,
  staffName: z.string(),
  active: z.boolean(),
  earnedPaise: nonNegativePaiseSchema,
  unsettledPaise: nonNegativePaiseSchema,
  unsettledJobCount: z.number().int().nonnegative(),
  advanceOutstandingPaise: nonNegativePaiseSchema,
  paidPaise: nonNegativePaiseSchema,
  outstandingPaise: paiseSchema,
  lastSettledAt: isoDateStringSchema.nullable(),
});
export type PayoutOverviewRow = z.infer<typeof payoutOverviewRowSchema>;

export const payoutOverviewSchema = z.object({
  from: isoDateStringSchema,
  to: isoDateStringSchema,
  rows: z.array(payoutOverviewRowSchema),
  totals: z.object({
    staffCount: z.number().int().nonnegative(),
    earnedPaise: nonNegativePaiseSchema,
    paidPaise: nonNegativePaiseSchema,
    unsettledPaise: nonNegativePaiseSchema,
    advanceOutstandingPaise: nonNegativePaiseSchema,
    /** The shop's wage liability: what it would cost to settle everybody this minute. */
    outstandingPaise: paiseSchema,
  }),
});
export type PayoutOverview = z.infer<typeof payoutOverviewSchema>;

// --- Requests --------------------------------------------------------------------------------------

export const payoutPeriodQuerySchema = z.object({
  from: dateInputSchema.optional(),
  to: dateInputSchema.optional(),
});
export type PayoutPeriodQuery = z.infer<typeof payoutPeriodQuerySchema>;

export const payoutListQuerySchema = payoutPeriodQuerySchema.extend({
  staffUserId: objectIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PayoutListQuery = z.infer<typeof payoutListQuerySchema>;

/** An addition or a deduction, recorded against a person and applied at their next settlement. */
export const recordPayoutAdjustmentSchema = z.object({
  kind: payoutAdjustmentKindSchema,
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  reason: payoutReasonSchema,
  occurredAt: dateInputSchema.optional(),
});
export type RecordPayoutAdjustmentInput = z.input<typeof recordPayoutAdjustmentSchema>;

/** Money handed over now, against work that has not been settled yet. */
export const recordAdvanceSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  method: paymentMethodSchema.default('cash'),
  paidAt: dateInputSchema.optional(),
  note: noteSchema.nullish(),
});
export type RecordAdvanceInput = z.input<typeof recordAdvanceSchema>;

/**
 * Settling: these jobs, this method, today.
 *
 * The jobs are named EXPLICITLY rather than by a date range. An owner ticks what they are
 * paying for and the server settles exactly that — a range would quietly sweep in a job
 * completed between the screen loading and the button being pressed, which is how a tailor ends
 * up paid for work they have not been shown.
 *
 * `expectedNetPaise` is the figure the screen showed. When it is sent and does not match what
 * the server computes, the settlement is refused: something moved between the two, and paying a
 * different number from the one the owner agreed to is worse than making them look again.
 */
export const settlePayoutSchema = z.object({
  assignmentIds: z.array(objectIdSchema).min(1, 'Choose at least one job').max(500),
  method: paymentMethodSchema.default('cash'),
  paidAt: dateInputSchema.optional(),
  note: noteSchema.nullish(),
  /** Recorded and applied in the same transaction — the bonus typed on the settlement screen. */
  adjustments: z
    .array(recordPayoutAdjustmentSchema.omit({ occurredAt: true }))
    .max(20)
    .default([]),
  /** Off only when the shop wants to leave an advance running for another fortnight. */
  applyAdvances: z.boolean().default(true),
  expectedNetPaise: nonNegativePaiseSchema.optional(),
});
export type SettlePayoutInput = z.input<typeof settlePayoutSchema>;

/** The statement, on the karigar's own phone. */
export const sendStatementSchema = z.object({
  /** Defaults to the number they sign in with. */
  to: z.string().trim().min(6).max(20).optional(),
  caption: z.string().trim().max(PAYOUT_NOTE_MAX_LENGTH).optional(),
});
export type SendStatementInput = z.input<typeof sendStatementSchema>;

export const payoutDownloadSchema = z.object({
  url: z.string().url(),
  expiresAt: isoDateStringSchema,
  filename: z.string(),
});
export type PayoutDownload = z.infer<typeof payoutDownloadSchema>;
